import { Injectable, signal } from '@angular/core';
import moment from 'moment-timezone';

export type DateTimeEmitMode = 'legacy' | 'structured';

export interface DateTimeFormatConfig {
  dateDisplayFormat: string;
  timeDisplayFormat: string;
  dateTimeDisplayFormat: string;
  dateParseFormats: string[];
  timeParseFormats: string[];
  dateTimeParseFormats: string[];
  legacyDatePayloadFormat: string;
  legacyTimePayloadFormat: string;
  legacyDateTimePayloadFormat: string;
}

export interface DateTimeValidationOptions {
  maxUtc?: string | null;
  futureDate?: boolean | string;
  futureDateTime?: boolean | string;
}

export interface DateTimeStructuredValue {
  rawInput: string;
  normalizedLocal: string | null;
  normalizedUtc: string | null;
  legacyValue: string | null;
  isValid: boolean;
  errors: string[];
}

export interface DateTimeUserPreference {
  dateDisplayFormat?: string;
  timeDisplayFormat?: string;
  dateTimeDisplayFormat?: string;
}

const DEFAULT_CONFIG: DateTimeFormatConfig = {
  dateDisplayFormat: 'MM/DD/YYYY',
  timeDisplayFormat: 'hh:mm A',
  dateTimeDisplayFormat: 'MM/DD/YYYY hh:mm A',
  dateParseFormats: ['M/D/YYYY', 'MM/DD/YYYY'],
  timeParseFormats: ['hh:mm A', 'h:mm A', 'HH:mm'],
  dateTimeParseFormats: ['M/D/YYYY hh:mm A', 'MM/DD/YYYY hh:mm A', 'M/D/YYYY h:mm A', 'MM/DD/YYYY h:mm A', 'M/D/YYYY HH:mm', 'MM/DD/YYYY HH:mm'],
  legacyDatePayloadFormat: 'YYYY-MM-DD',
  legacyTimePayloadFormat: 'HH:mm',
  legacyDateTimePayloadFormat: 'YYYY-MM-DD HH:mm',
};

@Injectable({
  providedIn: 'root',
})
export class DateTimeFormatService {
  private static readonly STORAGE_KEY = 'dateTimeFormatPreference';
  private readonly configSignal = signal<DateTimeFormatConfig>(DEFAULT_CONFIG);
  readonly config = this.configSignal.asReadonly();

  constructor() {
    this.loadPersistedPreference();
  }

  setConfig(partial: Partial<DateTimeFormatConfig>): void {
    this.configSignal.update((current) => ({
      ...current,
      ...partial,
    }));
  }

  setUserPreference(preference: DateTimeUserPreference, persist = true): void {
    const next: Partial<DateTimeFormatConfig> = {};
    const normalizedDateFormat = preference.dateDisplayFormat ? this.normalizeToFullYearFormat(preference.dateDisplayFormat) : undefined;
    const normalizedDateTimeFormat = preference.dateTimeDisplayFormat ? this.normalizeToFullYearFormat(preference.dateTimeDisplayFormat) : undefined;

    if (normalizedDateFormat) {
      next.dateDisplayFormat = normalizedDateFormat;
      next.dateParseFormats = this.getDateParseFormats(normalizedDateFormat);
    }

    if (preference.timeDisplayFormat) {
      next.timeDisplayFormat = preference.timeDisplayFormat;
      next.timeParseFormats = this.getTimeParseFormats(preference.timeDisplayFormat);
    }

    if (normalizedDateTimeFormat) {
      next.dateTimeDisplayFormat = normalizedDateTimeFormat;
      next.dateTimeParseFormats = this.getDateTimeParseFormats(normalizedDateTimeFormat);
    }

    this.setConfig(next);

    if (persist && typeof localStorage !== 'undefined') {
      localStorage.setItem(DateTimeFormatService.STORAGE_KEY, JSON.stringify(preference));
    }
  }

  initFromUserDetail(detail: any): void {
    const preference = this.extractPreference(detail);
    if (!preference) return;
    this.setUserPreference(preference);
  }

  private loadPersistedPreference(): void {
    if (typeof localStorage === 'undefined') return;
    const stored = localStorage.getItem(DateTimeFormatService.STORAGE_KEY);
    if (!stored) return;

    try {
      const parsed = JSON.parse(stored) as DateTimeUserPreference;
      this.setUserPreference(parsed, false);
    } catch {
      localStorage.removeItem(DateTimeFormatService.STORAGE_KEY);
    }
  }

  private extractPreference(detail: any): DateTimeUserPreference | null {
    if (!detail || typeof detail !== 'object') return null;

    const dateDisplayFormat =
      detail?.preferences?.dateDisplayFormat ||
      detail?.preferences?.date_format ||
      detail?.dateDisplayFormat ||
      detail?.date_format ||
      null;
    const timeDisplayFormat =
      detail?.preferences?.timeDisplayFormat ||
      detail?.preferences?.time_format ||
      detail?.timeDisplayFormat ||
      detail?.time_format ||
      null;
    const dateTimeDisplayFormat =
      detail?.preferences?.dateTimeDisplayFormat ||
      detail?.preferences?.datetime_format ||
      detail?.dateTimeDisplayFormat ||
      detail?.datetime_format ||
      null;

    if (!dateDisplayFormat && !timeDisplayFormat && !dateTimeDisplayFormat) {
      return null;
    }

    return {
      dateDisplayFormat: dateDisplayFormat || undefined,
      timeDisplayFormat: timeDisplayFormat || undefined,
      dateTimeDisplayFormat: dateTimeDisplayFormat || undefined,
    };
  }

  private getDateParseFormats(displayFormat: string): string[] {
    const normalized = (displayFormat || '').toUpperCase();
    if (normalized.includes('DD/MM')) return ['D/M/YYYY', 'DD/MM/YYYY'];
    return ['M/D/YYYY', 'MM/DD/YYYY'];
  }

  private getTimeParseFormats(displayFormat: string): string[] {
    const normalized = (displayFormat || '').toUpperCase();
    if (normalized.includes('HH')) return ['HH:mm', 'hh:mm A', 'h:mm A'];
    return ['hh:mm A', 'h:mm A', 'HH:mm'];
  }

  private getDateTimeParseFormats(displayFormat: string): string[] {
    const normalized = (displayFormat || '').toUpperCase();
    if (normalized.includes('DD/MM')) {
      return ['D/M/YYYY hh:mm A', 'DD/MM/YYYY hh:mm A', 'D/M/YYYY h:mm A', 'DD/MM/YYYY h:mm A', 'D/M/YYYY HH:mm', 'DD/MM/YYYY HH:mm'];
    }
    return ['M/D/YYYY hh:mm A', 'MM/DD/YYYY hh:mm A', 'M/D/YYYY h:mm A', 'MM/DD/YYYY h:mm A', 'M/D/YYYY HH:mm', 'MM/DD/YYYY HH:mm'];
  }

  autoFormatDateInput(value: string): string {
    const input = (value || '').trim();

    if (input.includes('/')) {
      const rawParts = input.split('/');
      const month = (rawParts[0] || '').replace(/\D/g, '').slice(0, 2);
      const secondPartDigits = (rawParts[1] || '').replace(/\D/g, '');
      const explicitYearDigits = (rawParts[2] || '').replace(/\D/g, '');

      // Allow users to keep typing continuously after first slash:
      // e.g. "04/102026" -> day=10, year=2026 (no need to type second slash manually)
      const day = secondPartDigits.slice(0, 2);
      const carriedYearDigits = secondPartDigits.slice(2, 6);
      const year = (explicitYearDigits || carriedYearDigits).slice(0, 4);

      let out = month;
      if (rawParts.length > 1 || day) out += `/${day}`;
      if (rawParts.length > 2 || year) out += `/${year}`;
      return out;
    }

    const digits = input.replace(/\D/g, '').slice(0, 8);
    const mm = digits.slice(0, 2);
    const dd = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);

    let out = mm;
    if (digits.length > 2) out += `/${dd}`;
    if (digits.length > 4) out += `/${yyyy}`;
    return out;
  }

  autoFormatTimeInput(value: string): string {
    const upper = (value || '').toUpperCase();
    const digits = upper.replace(/\D/g, '').slice(0, 4);
    const meridiemMatch = upper.match(/([AP]M?|AM|PM)\s*$/i);
    // Keep partial meridiem while typing (A/P) so backspace works naturally.
    const meridiem = meridiemMatch ? meridiemMatch[1].toUpperCase() : '';

    const hh = digits.slice(0, 2);
    const mm = digits.slice(2, 4);

    let out = hh;
    if (digits.length > 2) out += `:${mm}`;
    if (meridiem) out += ` ${meridiem}`;
    return out.trim();
  }

  autoFormatDateTimeInput(value: string): string {
    const raw = value || '';
    const upper = raw.toUpperCase();
    const digits = upper.replace(/\D/g, '');
    const meridiemMatch = upper.match(/([AP]M?|AM|PM)\s*$/i);
    // Keep partial meridiem while typing (A/P) so backspace works naturally.
    const meridiem = meridiemMatch ? meridiemMatch[1].toUpperCase() : '';

    const dateDigits = digits.slice(0, 8);
    const timeDigits = digits.slice(dateDigits.length, dateDigits.length + 4);

    const mm = dateDigits.slice(0, 2);
    const dd = dateDigits.slice(2, 4);
    const yyyy = dateDigits.slice(4, 8);

    let out = mm;
    if (dateDigits.length > 2) out += `/${dd}`;
    if (dateDigits.length > 4) out += `/${yyyy}`;

    if (timeDigits.length > 0) {
      const hh = timeDigits.slice(0, 2);
      const min = timeDigits.slice(2, 4);
      out += ` ${hh}`;
      if (timeDigits.length > 2) out += `:${min}`;
    }

    if (meridiem) out += ` ${meridiem}`;

    const userTypedTrailingSpace = /\s$/.test(raw);
    const hasCompleteDateOnly = /^\d{2}\/\d{2}\/\d{4}$/.test(out);
    if (userTypedTrailingSpace && hasCompleteDateOnly) {
      return `${out} `;
    }

    return out.trimEnd();
  }

  parseDateInput(value: string, timezone: string): moment.Moment | null {
    const parsed = moment.tz((value || '').trim(), this.config().dateParseFormats, true, timezone);
    return parsed.isValid() ? parsed : null;
  }

  parseTimeInput(value: string, timezone: string): moment.Moment | null {
    const parsed = moment.tz((value || '').trim().toUpperCase(), this.config().timeParseFormats, true, timezone);
    return parsed.isValid() ? parsed : null;
  }

  parseDateTimeInput(value: string, timezone: string): moment.Moment | null {
    const parsed = moment.tz((value || '').trim().toUpperCase(), this.config().dateTimeParseFormats, true, timezone);
    return parsed.isValid() ? parsed : null;
  }

  formatDateForDisplay(date: moment.Moment): string {
    return date.format(this.config().dateDisplayFormat);
  }

  formatTimeForDisplay(date: moment.Moment): string {
    return date.format(this.config().timeDisplayFormat);
  }

  formatDateTimeForDisplay(date: moment.Moment): string {
    return date.format(this.config().dateTimeDisplayFormat);
  }

  toLegacyDatePayload(date: moment.Moment): string {
    return date.format(this.config().legacyDatePayloadFormat);
  }

  toLegacyTimePayload(date: moment.Moment): string {
    return date.format(this.config().legacyTimePayloadFormat);
  }

  toLegacyDateTimePayloadUtc(date: moment.Moment): string {
    return date.clone().utc().format(this.config().legacyDateTimePayloadFormat);
  }

  resolveMaxMoment(timezone: string, options: DateTimeValidationOptions): moment.Moment | null {
    if (options.maxUtc) {
      const maxMoment = moment
        .utc(options.maxUtc, ['YYYY-MM-DD HH:mm:ss.SSS', 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm'])
        .tz(timezone);
      return maxMoment.isValid() ? maxMoment : null;
    }

    const futureDateTimeBlocked = options.futureDateTime === false || options.futureDateTime === 'false';
    if (futureDateTimeBlocked) {
      return moment().tz(timezone);
    }

    const futureDateBlocked = options.futureDate === false || options.futureDate === 'false';
    if (futureDateBlocked) {
      return moment().tz(timezone).endOf('day');
    }

    return null;
  }

  private normalizeToFullYearFormat(format: string): string {
    return (format || '').replace(/\bYY\b/g, 'YYYY');
  }
}
